/**
 * Pagination helper for the external PA orders worklist: loads the next page and appends rows without resetting the list.
 */
import { getBearerToken } from "../../../../../api/postCall/bearerToken";
import {
  searchForMedicalPaOrdersFromPostgres,
  SearchForMedicalPaOrdersFromPostgresParams,
} from "../../../../../api/postgreSQL/postCalls/searchForMedicalPaOrdersFromPostgres";
import { AuthStatusOptionModel } from "../../../../../data-model/authStatusOptions";
import { PostgresResponseForMedicalPaOrders } from "../../../../../data-model/postgresResponseForMedicalPaOrders";
import { ExternalTableState } from "../hooks/useExternalTableData";
import { generateAllPaOrdersForExternalByAlgoliaTableData } from "../utils/allPaOrdersForExternalByAlgoliaHelper";

/**
 * Fetches the next page of external PA orders from Postgres and appends the results
 * to the existing state and table data. Shows an inline loader during the fetch.
 *
 * @param payload - Search parameters including the target page number
 * @param authStatusOptions - Available auth status options for badge rendering
 * @param setExternalTableState - State setter for loading/data lifecycle (uses showinLineLoader)
 * @param setTableData - State setter that appends new rows to existing table data
 * @returns Promise that settles when the page request completes; failures are stored on `setExternalTableState` instead of throwing.
 */
export const makeAlgoliaCallForNextPage = async (
  payload: SearchForMedicalPaOrdersFromPostgresParams,
  authStatusOptions: AuthStatusOptionModel[],
  setExternalTableState: React.Dispatch<
    React.SetStateAction<ExternalTableState>
  >,
  setTableData: React.Dispatch<React.SetStateAction<any[]>>,
) => {
  setExternalTableState((prev) => ({ ...prev, showinLineLoader: true }));

  try {
    await getBearerToken();
    const response = await searchForMedicalPaOrdersFromPostgres(payload);
    const data = JSON.parse(response) as PostgresResponseForMedicalPaOrders;

    setExternalTableState((prev) => ({
      ...prev,
      data: {
        ...data,
        hits: [...(prev.data?.hits ?? []), ...(data.hits ?? [])],
      },
    }));

    const newTableData = generateAllPaOrdersForExternalByAlgoliaTableData(
      data.hits ?? [],
      authStatusOptions,
    );
    setTableData((prev) => [...prev, ...newTableData]);
  } catch (error) {
    setExternalTableState((prev) => ({ ...prev, error: error as string }));
  } finally {
    setExternalTableState((prev) => ({ ...prev, showinLineLoader: false }));
  }
};
